import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useLanguage } from "../LanguageProvider";
import { Header } from "../components/layout/Header";
import { PageLoader } from "../components/common/LoadingStates";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "../styles/pages/calendar.css";

export const SessionCalendar = () => {
  const { t, language } = useLanguage();
  const { sessions, loading, user } = useOutletContext();
  const navigate = useNavigate();

  const [currentDate, setCurrentDate] = useState(new Date());
  const calendarRef = useRef(null);

  useEffect(() => {
    // Auto-scroll to calendar on mobile when page loads
    if (window.innerWidth <= 768 && calendarRef.current && !loading) {
      const timer = setTimeout(() => {
        calendarRef.current.scrollIntoView({ behavior: "smooth" });
      }, 300); // Slight delay for layout stabilization
      return () => clearTimeout(timer);
    }
  }, [loading]);

  // Helper to get days in month
  const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const monthName = currentDate.toLocaleString(language === 'vi' ? 'vi-VN' : 'en-US', { month: 'long' });

  // Format date to YYYY-MM-DD in local time
  const toDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Map sessions to dates
  const sessionsByDate = sessions.reduce((acc, session) => {
    // If session_date is already YYYY-MM-DD, use it directly to avoid timezone issues
    const dateStr = session.session_date;
    if (!acc[dateStr]) acc[dateStr] = [];
    acc[dateStr].push(session);
    return acc;
  }, {});

  const days = [];
  // Padding for first day of week
  for (let i = 0; i < firstDay; i++) {
    days.push(<div key={`empty-${i}`} className="calendar-day other-month"></div>);
  }

  const todayStr = toDateStr(new Date());

  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    const dateStr = toDateStr(date);
    const daySessions = sessionsByDate[dateStr] || [];
    const isToday = dateStr === todayStr;

    days.push(
      <div
        key={d}
        className={`calendar-day ${isToday ? 'today' : ''} ${daySessions.length > 0 ? 'has-session' : ''}`}
        onClick={() => {
          if (daySessions.length > 0) {
            navigate(`/sessions/${daySessions[0].id}`);
          }
        }}
        style={{ cursor: daySessions.length > 0 ? 'pointer' : 'default' }}
      >
        <span className="day-number">{d}</span>
        <div className="day-sessions">
          {daySessions.map(session => (
            <div
              key={session.id}
              className={`calendar-session-tag ${user && session.created_by === user.id ? 'owned' : 'others'}`}
              onClick={(e) => {
                e.stopPropagation(); // Prevent trigger from parent day cell
                navigate(`/sessions/${session.id}`);
              }}
              title={session.session_name}
            >
              {session.session_name ? (session.session_name.length > 12 ? session.session_name.substring(0, 10) + '...' : session.session_name) : t("session_default_name")}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const weekdays = [
    language === 'vi' ? 'CN' : t("sunday").substring(0, 2).toUpperCase(),
    language === 'vi' ? 'T2' : t("monday").substring(0, 2).toUpperCase(),
    language === 'vi' ? 'T3' : t("tuesday").substring(0, 2).toUpperCase(),
    language === 'vi' ? 'T4' : t("wednesday").substring(0, 2).toUpperCase(),
    language === 'vi' ? 'T5' : t("thursday").substring(0, 2).toUpperCase(),
    language === 'vi' ? 'T6' : t("friday").substring(0, 2).toUpperCase(),
    language === 'vi' ? 'T7' : t("saturday").substring(0, 2).toUpperCase(),
  ];

  return (
    <PageLoader loading={loading}>
    <div className="calendar-page">
      <Header title={t("sessions_list")} subtitle={`${monthName} ${year}`} />

      <div className="container">
        <div className="calendar-container" ref={calendarRef}>
          <div className="calendar-header">
            <button className="calendar-nav-btn" onClick={prevMonth}><ChevronLeft size={20} /></button>
            <h2>{monthName} {year}</h2>
            <button className="calendar-nav-btn" onClick={nextMonth}><ChevronRight size={20} /></button>
          </div>

          <div className="calendar-weekdays">
            {weekdays.map((day, index) => (
              <div key={`weekday-${index}`} className="weekday">{day}</div>
            ))}
          </div>

          <div className="calendar-grid">
            {days}
          </div>
        </div>

      </div>
    </div>
    </PageLoader>
  );
};
